import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icon issue in React-Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface CollegeMapProps {
    collegeName: string;
    city: string | null;
    state: string | null;
    zip: string | null;
}

interface Coordinates {
    lat: number;
    lng: number;
}

export default function CollegeMap({ collegeName, city, state, zip }: CollegeMapProps) {
    const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const geocodeAddress = async () => {
            try {
                setLoading(true);
                setError(null);

                // Build address string
                const addressParts = [city, state, zip].filter(Boolean);
                if (addressParts.length === 0) {
                    setError('No location data available');
                    setLoading(false);
                    return;
                }

                const address = addressParts.join(', ');

                // Use Nominatim (OpenStreetMap) geocoding service
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
                    {
                        headers: {
                            'User-Agent': 'PolarisUI/1.0'
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error('Geocoding failed');
                }

                const data = await response.json();

                if (data && data.length > 0) {
                    setCoordinates({
                        lat: parseFloat(data[0].lat),
                        lng: parseFloat(data[0].lon)
                    });
                } else {
                    setError('Location not found');
                }
            } catch (err) {
                console.error('Geocoding error:', err);
                setError('Unable to load map');
            } finally {
                setLoading(false);
            }
        };

        geocodeAddress();
    }, [city, state, zip]);

    if (loading) {
        return (
            <div className="college-map-container">
                <div className="college-map-placeholder">
                    <div className="map-marker">📍</div>
                    <div className="map-info">Loading map...</div>
                </div>
                {city && state && zip && (
                    <div className="college-location-text">
                        📍 {city}, {state} {zip}
                    </div>
                )}
            </div>
        );
    }

    if (error || !coordinates) {
        return (
            <div className="college-map-container">
                <div className="college-map-placeholder">
                    <div className="map-marker">📍</div>
                    <div className="map-info">Map View</div>
                </div>
                {city && state && zip && (
                    <div className="college-location-text">
                        📍 {city}, {state} {zip}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="college-map-container">
            <MapContainer
                center={[coordinates.lat, coordinates.lng]}
                zoom={13}
                style={{ width: '100%', height: '380px', borderRadius: '8px' }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                />
                <Marker position={[coordinates.lat, coordinates.lng]}>
                    <Popup>
                        <strong>{collegeName}</strong>
                        <br />
                        {city}, {state} {zip}
                    </Popup>
                </Marker>
            </MapContainer>
            {city && state && zip && (
                <div className="college-location-info">
                    <div className="college-location-text">
                        📍 {city}, {state} {zip}
                    </div>
                    <a
                        href={`https://www.openstreetmap.org/?mlat=${coordinates.lat}&mlon=${coordinates.lng}#map=15/${coordinates.lat}/${coordinates.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-on-map-link"
                    >
                        View on map ↗
                    </a>
                </div>
            )}
        </div>
    );
}

